import { CdkScrollable } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule,Validators } from '@angular/forms';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatError,MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';

import { TInnerBot } from '@core/interfaces/TBot';

import {
  STATIC_ASSIGNMENT_WARNING_THRESHOLDS,
  TContextAssignment,
  TContextGroup,
  TContextItem,
} from '@core/api/context/types';
import { BotsService } from '@shared/services/bots.service';
import { ContextService } from '@shared/services/context.service';
import { innerBotMessages } from '@shared/utils/messages';

import { BotConfigHistoryModalComponent } from '../modals/bot-config-history-modal/bot-config-history-modal.component';

@Component({
    selector: 'app-inner-bot-modal',
    templateUrl: './inner-bot-modal.component.html',
    styleUrls: ['./inner-bot-modal.component.scss'],
    standalone: true,
    imports: [
      CdkScrollable,
      CommonModule,
      ReactiveFormsModule,
      MatFormField,
      MatOption,
      MatInput,
      MatError,
      MatIcon,
      MatTooltip,
      MatSlideToggle,
      MatButton,
      MatCheckbox,
      MatHint,
      MatSelect,
      MatIconButton,
      MatMiniFabButton,
      NgSelectModule,
    ]
})
export class InnerBotModalComponent implements OnInit, OnDestroy {
  public newBotForm: FormGroup;
  public innerBotMessages = innerBotMessages;
  public availableInnerBots: TInnerBot[] = [];
  protected availableContextGroups: TContextGroup[] = [];
  protected availableContextItems: TContextItem[] = [];
  protected staticAssignmentCharCount = 0;
  protected readonly warningThresholds = STATIC_ASSIGNMENT_WARNING_THRESHOLDS;
  private subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<InnerBotModalComponent>,
    private _botService: BotsService,
    private _contextService: ContextService,
    private _dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: { botCreatorId: string; bot?: TInnerBot; innerBotsSnapshot?: TInnerBot[] }
  ) {
    this.newBotForm = this.fb.nonNullable.group({
      name: ['', [Validators.required]],
      description: [''],
      preSearch: [''],
      behaviour: [''],
      instruction: [''],
      config: [''],
      includeInitContext: [true],
      includeTools: [false],
      includePreviousBots: this.fb.array([]),
      outputDestinationType: ['frontend'],
      contextAssignments: this.fb.array([]),
    });
  }

  public ngOnInit(): void {
    this.applyAvailableInnerBots(this.data.innerBotsSnapshot);

    // Shared BehaviorSubject can hold another screen's bot creator; only apply when ids match.
    this.subs.push(
      this._botService.getBotCreatorObservable().subscribe(botCreator => {
        if (botCreator._id !== this.data.botCreatorId) {
          return;
        }
        this.applyAvailableInnerBots(botCreator.bots);
      })
    );

    // Load context groups
    this._contextService.fetchGroups();
    this.subs.push(
      this._contextService.groups$.subscribe(groups => {
        this.availableContextGroups = groups || [];
      })
    );

    // Load ALL context items
    this.subs.push(
      this._contextService.getAllItems().subscribe({
        next: items => {
          this.availableContextItems = items || [];
        },
        error: err => {
          console.error('Error loading context items:', err);
          this.availableContextItems = [];
        },
      })
    );

    if (this.data.bot) {
      this.newBotForm.patchValue({
        name: this.data.bot.name,
        description: this.data.bot.description,
        preSearch: this.data.bot.preSearch,
        instruction: this.data.bot.instruction,
        behaviour: this.data.bot.behaviour,
        config: this.data.bot.config,
        includeInitContext: this.data.bot.inputDataSource?.includeInitContext ?? true,
        includeTools: this.data.bot.inputDataSource?.includeTools ?? false,
        outputDestinationType: this.data.bot.outputDestination?.type ?? 'frontend',
      });

      // Load includePreviousBots
      if (this.data.bot.inputDataSource?.includePreviousBots) {
        this.data.bot.inputDataSource.includePreviousBots.forEach(prevBot => {
          this.addPreviousBot(
            this.resolvePreviousBotId(prevBot),
            prevBot.includeConversation,
            prevBot.includeSearchedSources
          );
        });
      }

      // Load contextAssignments
      const contextAssignmentsArray = this.contextAssignmentsArray;
      contextAssignmentsArray.clear();
      if ((this.data.bot as any).contextAssignments && (this.data.bot as any).contextAssignments.length > 0) {
        (this.data.bot as any).contextAssignments.forEach((assignment: TContextAssignment) => {
          contextAssignmentsArray.push(this.createContextAssignmentGroup(assignment));
        });
      }
      this.calculateStaticCharCount();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  private applyAvailableInnerBots(bots: TInnerBot[] | undefined): void {
    const list = bots ?? [];
    this.availableInnerBots = list.filter(
      bot => !this.data.bot || bot._id !== this.data.bot._id
    );
  }

  private resolvePreviousBotId(previousBot: {
    botId?: unknown;
    botName?: unknown;
    name?: unknown;
    ref?: unknown;
  }): string {
    const candidates = [
      previousBot.botId,
      previousBot.botName,
      previousBot.name,
      previousBot.ref,
    ]
      .map(value => {
        if (!value) {
          return '';
        }
        if (typeof value === 'object') {
          const objectValue = value as { _id?: unknown; id?: unknown; name?: unknown };
          return String(objectValue._id ?? objectValue.id ?? objectValue.name ?? '');
        }
        return String(value);
      })
      .filter(Boolean);

    for (const candidate of candidates) {
      const match = this.availableInnerBots.find(
        bot => bot._id === candidate || bot.name === candidate
      );
      if (match) {
        return match._id;
      }
    }

    return typeof previousBot.botId === 'string' ? previousBot.botId : '';
  }

  public get name() {
    return this.newBotForm.get('name')!;
  }

  public get description() {
    return this.newBotForm.get('description')!;
  }

  public get behaviour() {
    return this.newBotForm.get('behaviour')!;
  }

  public get preSearch() {
    return this.newBotForm.get('preSearch')!;
  }

  public get instruction() {
    return this.newBotForm.get('instruction')!;
  }

  public get config() {
    return this.newBotForm.get('config')!;
  }

  public get includePreviousBots(): FormArray {
    return this.newBotForm.get('includePreviousBots') as FormArray;
  }

  public addPreviousBot(
    botId = '',
    includeConversation = true,
    includeSearchedSources = false
  ): void {
    const botGroup = this.fb.group({
      botId: [botId, Validators.required],
      includeConversation: [includeConversation],
      includeSearchedSources: [includeSearchedSources],
    });
    this.includePreviousBots.push(botGroup);
  }

  public removePreviousBot(index: number): void {
    this.includePreviousBots.removeAt(index);
  }

  public addOrUpdateBot(): void {
    if (this.newBotForm.invalid) {
      return;
    }
    const fv = this.newBotForm.value;
    const requestBody: any = {
      name: fv.name || '',
      description: fv.description,
      instruction: fv.instruction,
      preSearch: fv.preSearch,
      behaviour: fv.behaviour,
      config: fv.config,
      inputDataSource: {
        includeInitContext: fv.includeInitContext,
        includeTools: fv.includeTools,
        includePreviousBots: fv.includePreviousBots || [],
      },
      outputDestination: {
        type: fv.outputDestinationType,
      },
      contextAssignments: this.getContextAssignmentsValue(),
    };
    if (this.data.bot) {
      this._botService.editBot({ id: this.data.bot._id, ...requestBody }, () => {
        this.dialogRef.close();
      });
    } else {
      this._botService.addInnerBot(this.data.botCreatorId, requestBody, () => {
        this.dialogRef.close();
      });
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  // Context Assignments helpers
  get contextAssignmentsArray(): FormArray {
    return this.newBotForm.get('contextAssignments') as FormArray;
  }

  private createContextAssignmentGroup(assignment?: TContextAssignment): FormGroup {
    return this.fb.group({
      targetType: [assignment?.targetType || 'group', Validators.required],
      targetId: [assignment?.targetId || '', Validators.required],
      assignmentType: [assignment?.assignmentType || 'dynamic', Validators.required],
    });
  }

  addContextAssignment(): void {
    this.contextAssignmentsArray.push(this.createContextAssignmentGroup());
  }

  removeContextAssignment(index: number): void {
    this.contextAssignmentsArray.removeAt(index);
    this.calculateStaticCharCount();
  }

  getContextAssignmentGroup(index: number): FormGroup {
    return this.contextAssignmentsArray.at(index) as FormGroup;
  }

  onAssignmentChange(): void {
    this.calculateStaticCharCount();
  }

  onTargetTypeChange(index: number): void {
    // Clear the target selection when type changes
    const assignmentGroup = this.getContextAssignmentGroup(index);
    assignmentGroup.get('targetId')?.setValue('');
    this.calculateStaticCharCount();
  }

  calculateStaticCharCount(): void {
    let totalChars = 0;
    const assignments = this.contextAssignmentsArray.value as TContextAssignment[];

    assignments.forEach(assignment => {
      if (assignment.assignmentType === 'static') {
        if (assignment.targetType === 'group') {
          const group = this.availableContextGroups.find(g => g._id === assignment.targetId);
          if (group) {
            // Estimate chars from group instruction + items
            totalChars += group.instruction?.length || 0;
            // Add estimated item content (would need to fetch items for accurate count)
            totalChars += (group.itemCount || 0) * 500; // Rough estimate
          }
        } else {
          const item = this.availableContextItems.find(i => i._id === assignment.targetId);
          if (item) {
            totalChars +=
              item.question.length + item.content.length + (item.instruction?.length || 0);
          }
        }
      }
    });

    this.staticAssignmentCharCount = totalChars;
  }

  getStaticWarningLevel(): 'none' | 'yellow' | 'orange' | 'red' {
    if (this.staticAssignmentCharCount >= this.warningThresholds.red) {
      return 'red';
    } else if (this.staticAssignmentCharCount >= this.warningThresholds.orange) {
      return 'orange';
    } else if (this.staticAssignmentCharCount >= this.warningThresholds.yellow) {
      return 'yellow';
    }
    return 'none';
  }

  getStaticWarningMessage(): string {
    const level = this.getStaticWarningLevel();
    const tokens = Math.round(this.staticAssignmentCharCount / 4);
    switch (level) {
      case 'red':
        return `Warning: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens). This is very large and may impact performance.`;
      case 'orange':
        return `Caution: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens). Consider using dynamic assignment.`;
      case 'yellow':
        return `Note: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens).`;
      default:
        return '';
    }
  }

  private getContextAssignmentsValue(): TContextAssignment[] | undefined {
    const assignments = this.contextAssignmentsArray.value as TContextAssignment[];
    if (!assignments || assignments.length === 0) {
      return undefined;
    }
    return assignments.filter(a => a.targetId && a.targetType && a.assignmentType);
  }

  protected showHistoryModal() {
    this._dialog.open(BotConfigHistoryModalComponent, {
      data: {
        bot: this.data.bot,
        fieldSet: 'innerBot',
      },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'bot-config-history-modal',
      hasBackdrop: true,
      disableClose: false,
    });
  }
}
